// Demo 2: Restaurant OS — Multi-language
// Fictional brand: "Taverna Dojran" — family taverna on Dojran lake, near Gevgelija
// Pitch: reservations + menu + voice AI answering calls + loyalty — all in MK/EN/GR

import { useState } from 'react'
import DemoLayout from './shared/DemoLayout'
import { useT } from './shared/i18n-ctx'
import type { Dict } from './shared/i18n-ctx'

const dict: Dict = {
  hero_tag: { en: 'Online reservations · Multi-language menu · 24/7 AI phone', mk: 'Онлајн резервации · Мени на 3 јазици · AI телефон 24/7', gr: 'Online κρατήσεις · Μενού 3 γλωσσών · AI τηλέφωνο 24/7' },
  hero_title: { en: 'Demo · Restaurant', mk: 'Демо · Ресторан', gr: 'Demo · Εστιατόριο' },
  hero_sub: {
    en: 'Template for any restaurant or taverna. Online menu, table reservation, multi-language. The owner replaces the logo, colors, menu — system stays the same.',
    mk: 'Шаблон за било кој ресторан или таверна. Онлајн мени, резервација на маса, повеќе јазици. Сопственикот го менува логото, боите, менито — системот е ист.',
    gr: 'Πρότυπο για εστιατόριο ή ταβέρνα. Online μενού, κράτηση τραπεζιού, πολύγλωσσο. Ο ιδιοκτήτης αλλάζει logo, χρώματα, μενού.',
  },
  book_table: { en: 'Book a table', mk: 'Резервирај маса', gr: 'Κλείσε τραπέζι' },
  menu: { en: 'Menu', mk: 'Мени', gr: 'Μενού' },
  reserve_title: { en: 'Reserve your table', mk: 'Резервирај своја маса', gr: 'Κράτηση τραπεζιού' },
  name: { en: 'Your name', mk: 'Твое име', gr: 'Όνομα' },
  phone: { en: 'Phone', mk: 'Телефон', gr: 'Τηλέφωνο' },
  date: { en: 'Date', mk: 'Датум', gr: 'Ημερομηνία' },
  time: { en: 'Time', mk: 'Време', gr: 'Ώρα' },
  party: { en: 'People', mk: 'Лица', gr: 'Άτομα' },
  confirm: { en: 'Confirm reservation', mk: 'Потврди резервација', gr: 'Επιβεβαίωση' },
  submitted: {
    en: '✓ Table booked! We sent an SMS confirmation.',
    mk: '✓ Масата е резервирана! Испративме СМС потврда.',
    gr: '✓ Η κράτηση ολοκληρώθηκε! Στείλαμε SMS επιβεβαίωσης.',
  },
  voice_title: { en: 'AI receptionist that speaks your language', mk: 'AI рецепционер кој зборува твојот јазик', gr: 'AI ρεσεψιονίστ που μιλά τη γλώσσα σου' },
  voice_sub: {
    en: 'Can\'t book online? Call us at any hour. Our AI answers in English, Macedonian, or Greek and books your table instantly. No more "leave a message."',
    mk: 'Не можеш онлајн? Јави се во било кој час. Нашиот AI одговара на англиски, македонски или грчки и резервира веднаш.',
    gr: 'Δεν μπορείς online; Κάλεσε οποιαδήποτε ώρα. Το AI απαντά στα αγγλικά, μακεδονικά ή ελληνικά και κλείνει αμέσως.',
  },
  loyalty_title: { en: 'Every 10th meal is on us', mk: 'Секој десетти оброк е на сметка на нас', gr: 'Κάθε 10ο γεύμα είναι δικό μας' },
  loyalty_sub: {
    en: 'Scan the QR at your table. Collect stamps. Free meal at 10. Birthday dessert on the house.',
    mk: 'Скенирај QR на масата. Собирај печати. Бесплатен оброк на 10. Десерт на родендeн бесплатно.',
    gr: 'Σκανάρισε το QR. Μάζεψε σφραγίδες. Δωρεάν γεύμα στα 10. Επιδόρπιο γενεθλίων δωρεάν.',
  },
  menu_starter: { en: 'Starters', mk: 'Предјадења', gr: 'Ορεκτικά' },
  menu_main: { en: 'Mains', mk: 'Главни јадења', gr: 'Κυρίως' },
  menu_dessert: { en: 'Desserts', mk: 'Десерти', gr: 'Επιδόρπια' },
}

const MENU = {
  starter: [
    { name: { en: 'Ajvar & feta', mk: 'Ајвар со сирење', gr: 'Αϊβάρ & φέτα' }, price: 4 },
    { name: { en: 'Lake smoked fish plate', mk: 'Плата чадена езерска риба', gr: 'Καπνιστά ψάρια λίμνης' }, price: 9 },
    { name: { en: 'Shopska salad', mk: 'Шопска салата', gr: 'Σαλάτα σόπσκα' }, price: 5 },
  ],
  main: [
    { name: { en: 'Grilled lake fish (per 100g)', mk: 'Печена езерска риба (по 100г)', gr: 'Ψάρι λίμνης στα κάρβουνα (100γρ)' }, price: 3.5 },
    { name: { en: 'Kebapi with fries', mk: 'Ќебапи со помфрит', gr: 'Κεμπάπι με πατάτες' }, price: 7 },
    { name: { en: 'Stuffed peppers', mk: 'Полнети пиперки', gr: 'Γεμιστές πιπεριές' }, price: 6.5 },
    { name: { en: 'Wood-oven lamb', mk: 'Јагнешко од фурна', gr: 'Αρνί από ξυλόφουρνο' }, price: 14 },
  ],
  dessert: [
    { name: { en: 'Baklava', mk: 'Баклава', gr: 'Μπακλαβάς' }, price: 3.5 },
    { name: { en: 'Tulumbi', mk: 'Тулумби', gr: 'Τουλούμπες' }, price: 3 },
    { name: { en: 'Homemade rakija shot', mk: 'Домашна ракија', gr: 'Σπιτική ρακή' }, price: 2 },
  ],
} as const

export default function RestaurantDemo() {
  return (
    <DemoLayout
      dict={dict}
      accent="from-orange-500 to-red-600"
      chatPersona={{
        name: 'Restaurant',
        kind: 'booking',
        items: [
          { name: 'Table for 2', price: 0 },
          { name: 'Table for 4', price: 0 },
          { name: 'Bruschetta', price: 5 },
          { name: 'Grilled sea bass', price: 16 },
          { name: 'Ribeye steak', price: 22 },
          { name: 'Tiramisu', price: 6 },
        ],
      }}
    >
      <Body />
    </DemoLayout>
  )
}

function Body() {
  const { t, lang } = useT()
  const [party, setParty] = useState(2)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('19:30')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)

  function reserve(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div className="bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900">
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1600&q=70)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <span className="chip bg-orange-500/10 text-orange-400 border border-orange-500/20 mb-5">
            🍴 {t('hero_tag')}
          </span>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white max-w-3xl">{t('hero_title')}</h1>
          <p className="mt-5 text-lg text-ink-100 max-w-2xl">{t('hero_sub')}</p>
          <div className="mt-6">
            <a href="#reserve" className="btn-primary">{t('book_table')}</a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-white mb-8">{t('menu')}</h2>
        <div className="grid gap-6 lg:grid-cols-3">
          {(['starter', 'main', 'dessert'] as const).map((section) => (
            <div key={section} className="card">
              <h3 className="font-semibold text-white text-lg mb-4">{t(`menu_${section}`)}</h3>
              <ul className="space-y-3">
                {MENU[section].map((item, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-ink-100">{item.name[lang]}</span>
                    <span className="text-ink-500 font-mono">€{item.price.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section id="reserve" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6">{t('reserve_title')}</h2>
          {submitted ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-4 text-center font-medium">
              {t('submitted')}
            </div>
          ) : (
            <form onSubmit={reserve} className="grid gap-4 sm:grid-cols-2">
              <label><span className="label">{t('name')}</span><input required className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
              <label><span className="label">{t('phone')}</span><input required type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
              <label><span className="label">{t('date')}</span><input required type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} /></label>
              <label><span className="label">{t('time')}</span><input required type="time" className="input" value={time} onChange={(e) => setTime(e.target.value)} /></label>
              <label className="sm:col-span-2"><span className="label">{t('party')}</span><input required type="number" min={1} max={20} className="input" value={party} onChange={(e) => setParty(+e.target.value || 1)} /></label>
              <button type="submit" className="btn-primary sm:col-span-2">{t('confirm')}</button>
            </form>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-24">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="card">
            <div className="text-4xl mb-3">📞</div>
            <h3 className="text-xl font-semibold text-white">{t('voice_title')}</h3>
            <p className="mt-2 text-ink-100">{t('voice_sub')}</p>
          </div>
          <div className="card">
            <div className="text-4xl mb-3">🎁</div>
            <h3 className="text-xl font-semibold text-white">{t('loyalty_title')}</h3>
            <p className="mt-2 text-ink-100">{t('loyalty_sub')}</p>
          </div>
        </div>
      </section>
    </div>
  )
}
